import { useQuery } from '@apollo/client/react';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';

import { GetOrganizationNotificationSettingDocument } from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import {
  notificationSettingsState,
  notificationState,
  telemedicineAppointmentsState,
  uberDeliveryNotificationsState,
} from '~/state/layouts/SharedAppShell/atoms';
import { publicPath } from '~/utils/path';

import { useNotificationSound } from './use-notification-sound';
import { useSubscriptionNotifications } from './use-subscription-notifications';

const changeFaviconBadge = (count: number) => {
  const canvasLength = 48;
  const favicon = document.querySelector<HTMLLinkElement>('#favicon');
  const image = document.createElement('img');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context || !favicon || !image) {
    return;
  }

  canvas.width = canvas.height = canvasLength;
  image.crossOrigin = 'Anonymous';
  image.onload = () => {
    const fontSize = 20;
    const text = `${count > 99 ? '99+' : count}`;
    const badgeRadius = 16;
    const badgeCenterPos = canvasLength - badgeRadius;

    context.clearRect(0, 0, 48, 48);
    context.drawImage(image, 0, 0, 48, 48, 0, 0, canvasLength, canvasLength);

    if (count > 0) {
      context.beginPath();
      context.fillStyle = '#ff2020';
      context.arc(badgeCenterPos, badgeCenterPos, badgeRadius, 0, Math.PI * 2, true);
      context.fill();
      context.font = `bold ${fontSize}px Arial, meiryo, sans-serif`;
      context.textAlign = 'center';
      context.fillStyle = '#ffffff';
      context.fillText(text, badgeCenterPos, badgeCenterPos + fontSize / 2 - 2); // -2してるのはうまくセンターにはまらないので微調整したため
    }

    favicon.href = canvas.toDataURL();
  };

  image.src = publicPath('/favicon.ico');
};

const computeNotifications = (
  isLightPlan: boolean,
  settings: { followup: boolean; online: boolean },
  count: {
    followup: number;
    notification: number;
    entry: number;
    telemedicine: number;
    uberDelivery: number;
    medicineNote: number;
  },
) => {
  let badgeCount = count.notification + count.medicineNote + count.entry;
  let unreadCount = count.notification + count.entry;

  if (settings.online) {
    unreadCount += count.telemedicine;
  }

  if (!isLightPlan) {
    if (settings.followup) {
      unreadCount += count.followup;
    }
    badgeCount = badgeCount + count.followup;
  }

  unreadCount += count.uberDelivery;

  return {
    badgeCount,
    unreadCount,
  };
};

export const useManageNotifications = () => {
  const [{ appointments }, setTelemedicineAppointmentsState] = useAtom(
    telemedicineAppointmentsState,
  );
  const notificationCountState = useAtomValue(notificationState);
  const { needPlaySoundNotificationCount } = useAtomValue(uberDeliveryNotificationsState);
  const [{ isLightPlan, ...settings }, setNotificationSettingsState] =
    useAtom(notificationSettingsState);
  const { loading } = useSubscriptionNotifications();
  const { data } = useQuery(GetOrganizationNotificationSettingDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!data) return;

    const isLightPlan = getMe(data)?.organization.company.contractLightPlan ?? false;
    const settingData = getMe(data)?.organization.notificationSetting?.data;
    const isFollowupEnabled = !!settingData?.medicationFollowupEnabled;
    const isOnlineEnabled = !!settingData?.onlineEnabled;

    setTelemedicineAppointmentsState((_state) => ({ ..._state, isEnabled: isOnlineEnabled }));
    setNotificationSettingsState({ isLightPlan, isFollowupEnabled, isOnlineEnabled });
  }, [data, setNotificationSettingsState, setTelemedicineAppointmentsState]);

  const count = useMemo(
    () => ({
      followup: notificationCountState.followupCount,
      notification: notificationCountState.receptionCount,
      entry: notificationCountState.entryCount,
      telemedicine: appointments.length,
      uberDelivery: needPlaySoundNotificationCount,
      medicineNote: notificationCountState.medicineNoteCount,
    }),
    [
      appointments.length,
      needPlaySoundNotificationCount,
      notificationCountState.followupCount,
      notificationCountState.entryCount,
      notificationCountState.receptionCount,
      notificationCountState.medicineNoteCount,
    ],
  );

  const sound = data ? getMe(data)?.organization.notificationSetting?.data.sound : undefined;
  const { play } = useNotificationSound(sound);

  const { badgeCount, unreadCount } = useMemo(
    () =>
      computeNotifications(
        isLightPlan,
        {
          followup: settings.isFollowupEnabled,
          online: settings.isOnlineEnabled,
        },
        count,
      ),
    [count, isLightPlan, settings.isFollowupEnabled, settings.isOnlineEnabled],
  );

  useEffect(() => {
    changeFaviconBadge(badgeCount);
  }, [badgeCount]);

  useEffect(() => {
    if (!loading) {
      play(unreadCount);
    }
  }, [loading, play, unreadCount]);
};
